import { FormEvent, useMemo, useState } from "react";
import rocket from "../../../assets/rocket.png";
import { useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import { affiliateService } from "../../../services/affiliate.service";

export function EmailConfirmation() {
  const location = useLocation();
  const emailFromState = location.state?.email;
  const email = typeof emailFromState === "string" ? emailFromState : "";
  const [code, setCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const displayEmail = useMemo(
    () => (email ? email : "your email"),
    [email]
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!email) {
      toast.error("We couldn't detect your email. Please sign in and try again.");
      return;
    }

    if (code.trim().length !== 6) {
      toast.error("Enter the 6-digit code from your email.");
      return;
    }

    try {
      setIsSubmitting(true);
      await affiliateService.verifyEmailCode(email, code.trim());
      toast.success("Email verified successfully! You can now sign in.");
      setCode("");
    } catch (error) {
      console.error("Error verifying email:", error);
      toast.error("Verification failed. Double-check the code and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto px-4 sm:px-0">
      {/* Header with Rocket Icon */}
      <div className="text-center mb-6 sm:mb-8">
        <div className="inline-block mb-4 sm:mb-6">
          <img src={rocket} alt="" className="h-12 w-12" />
        </div>
        <h1 className="text-xl sm:text-2xl font-semibold mb-2">
          Almost there, confirm your email!
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          We've sent a verification code to
          <br />
          <span className="font-medium text-foreground">{displayEmail}</span>
        </p>
      </div>

      {/* Instructions */}
      <div className="mb-6 sm:mb-8">
        <h2 className="text-sm sm:text-base font-medium mb-2 sm:mb-3">
          What's Next?
        </h2>
        <ul className="space-y-2 text-sm sm:text-base text-muted-foreground">
          <li>• Open your email inbox (check spam or promotions if needed).</li>
          <li>• Copy the 6-digit verification code.</li>
          <li>• Enter it below to activate your affiliate account.</li>
        </ul>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3 mb-6">
        <label className="block text-sm font-medium text-muted-foreground">
          Verification code
        </label>
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={code}
          onChange={(event) =>
            setCode(event.target.value.replace(/[^0-9]/g, ""))
          }
          placeholder="Enter 6-digit code"
          className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />
        <button
          type="submit"
          disabled={isSubmitting || !email}
          className="w-full inline-flex justify-center items-center px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:bg-brand-300 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? "Verifying..." : "Verify email"}
          </button>
      </form>

      {/* Help */}
      <div className="text-center text-xs sm:text-sm text-muted-foreground">
        <p className="mb-2">
          Not seeing the email? Add contact@reveshare.com to your contacts and
          request a new code from your dashboard.
        </p>
      </div>
    </div>
  );
}
